// Sample seed datasets for the calendar playground.
//
// Each profile returns a single dense seed (today + scattered through the
// surrounding month) anchored at the given date. Built ONCE on connect and
// when the user picks a different profile — never regenerated on plain
// navigation. So:
//   - User-created events survive prev/next/view-switch (until page reload).
//   - Empty days/weeks/months are honestly empty.
//   - Drill-down from Month → Day shows exactly the events the cell suggested.

const SAMPLE_KEYS = ["small_org", "large_org", "solo", "empty", "busy"];

export const SAMPLE_LABELS = {
  small_org: "3-staff org",
  large_org: "8-staff org",
  solo: "Solo practitioner",
  empty: "Empty calendar",
  busy: "Busy with overlaps",
};

export function listSampleKeys() {
  return [...SAMPLE_KEYS];
}

// Build a seed for the given profile, anchored at `anchorDate` (YYYY-MM-DD).
// `anchorDate` is treated as "today" — it gets a handful of events so the
// default Day view always has content. Other days/weeks of the month also
// get scattered events to populate Week and Month views.
export function buildSampleSeed(profileKey, anchorDate = todayString()) {
  switch (profileKey) {
    case "large_org":
      return buildLargeOrg(anchorDate);
    case "solo":
      return buildSolo(anchorDate);
    case "empty":
      return { staff: [], events: [] };
    case "busy":
      return buildBusy(anchorDate);
    case "small_org":
    default:
      return buildSmallOrg(anchorDate);
  }
}

// Back-compat shim — earlier playground code imported this directly.
export function buildSmallOrgSeed(anchorDate = todayString()) {
  return buildSmallOrg(anchorDate);
}

// ---- profiles ----

function buildSmallOrg(anchor) {
  const staff = [
    { id: "1", name: "John Stylist", avatarUrl: null },
    { id: "2", name: "Jane Colorist", avatarUrl: null },
    { id: "3", name: "Alex Barber", avatarUrl: null },
  ];
  return { staff, events: smallOrgEvents(anchor) };
}

function buildLargeOrg(anchor) {
  const staff = [
    { id: "1", name: "John Stylist", avatarUrl: null },
    { id: "2", name: "Jane Colorist", avatarUrl: null },
    { id: "3", name: "Alex Barber", avatarUrl: null },
    { id: "4", name: "Mia Cutter", avatarUrl: null },
    { id: "5", name: "Sam Trim", avatarUrl: null },
    { id: "6", name: "Noah Style", avatarUrl: null },
    { id: "7", name: "Ava Color", avatarUrl: null },
    { id: "8", name: "Leo Fade", avatarUrl: null },
  ];
  return { staff, events: largeOrgEvents(anchor) };
}

function buildSolo(anchor) {
  const staff = [{ id: "1", name: "Riley Solo", avatarUrl: null }];
  return { staff, events: soloEvents(anchor) };
}

function buildBusy(anchor) {
  const staff = [
    { id: "1", name: "John Stylist", avatarUrl: null },
    { id: "2", name: "Jane Colorist", avatarUrl: null },
    { id: "3", name: "Alex Barber", avatarUrl: null },
  ];
  return { staff, events: busyEvents(anchor) };
}

// ---- event generators ----

function smallOrgEvents(anchor) {
  return [
    ...todayEvents(anchor),
    ...monthScatter(anchor, [
      { offset: 1, hour: 14, minute: 0, staffId: "2", status: "confirmed", client: "Carlos Reyes", service: "Trim" },
      { offset: 3, hour: 9, minute: 0, staffId: "3", status: "confirmed", client: "Olivia Tan", service: "Highlights consult" },
      { offset: 7, hour: 9, minute: 0, staffId: "1", status: "confirmed", client: "Henry Park", service: "Cut" },
      { offset: 7, hour: 10, minute: 30, staffId: "2", status: "pending", client: "Lucia Vega", service: "Color" },
      { offset: 7, hour: 13, minute: 0, staffId: "3", status: "confirmed", client: "Pat Morales", service: "Style" },
      { offset: 7, hour: 15, minute: 0, staffId: "1", status: "completed", client: "Andres Lim", service: "Cut" },
      { offset: 7, hour: 16, minute: 30, staffId: "2", status: "confirmed", client: "Rosa Vega", service: "Trim" },
      { offset: 9, hour: 11, minute: 0, staffId: "1", status: "completed", client: "Helen Park", service: "Color" },
      { offset: 14, hour: 10, minute: 0, staffId: "2", status: "confirmed", client: "Mark Aquino", service: "Cut" },
      { offset: 21, hour: 14, minute: 0, staffId: "1", status: "pending", client: "Rosa Vega", service: "Highlights" },
      { offset: 23, hour: 15, minute: 30, staffId: "2", status: "cancelled", client: "No-show", service: "Cancelled" },
      { offset: 27, hour: 9, minute: 30, staffId: "3", status: "confirmed", client: "Helen Park", service: "Cut" },
    ]),
  ];
}

function largeOrgEvents(anchor) {
  // Spread events across all 8 staff so Day view always shows variety.
  return [
    ...todayEvents(anchor),
    ...monthScatter(anchor, [
      { offset: 0, hour: 11, minute: 0, staffId: "4", status: "confirmed", client: "Mia Walk-in", service: "Cut" },
      { offset: 0, hour: 14, minute: 0, staffId: "5", status: "confirmed", client: "Sam Walk-in", service: "Trim" },
      { offset: 0, hour: 15, minute: 0, staffId: "6", status: "pending", client: "Noah Walk-in", service: "Style" },
      { offset: 0, hour: 16, minute: 0, staffId: "7", status: "confirmed", client: "Ava Walk-in", service: "Color" },
      { offset: 0, hour: 12, minute: 0, staffId: "8", status: "confirmed", client: "Leo Walk-in", service: "Fade" },
      { offset: 2, hour: 10, minute: 0, staffId: "5", status: "confirmed", client: "James Park", service: "Cut" },
      { offset: 2, hour: 14, minute: 0, staffId: "6", status: "pending", client: "Anna Lee", service: "Color" },
      { offset: 4, hour: 9, minute: 0, staffId: "7", status: "confirmed", client: "Diego Cruz", service: "Trim" },
      { offset: 4, hour: 13, minute: 30, staffId: "8", status: "confirmed", client: "Mark Lim", service: "Fade" },
      { offset: 8, hour: 11, minute: 0, staffId: "4", status: "completed", client: "Sara Tan", service: "Cut" },
      { offset: 11, hour: 15, minute: 0, staffId: "1", status: "confirmed", client: "Tina Park", service: "Highlights" },
      { offset: 16, hour: 10, minute: 0, staffId: "5", status: "pending", client: "Jose Reyes", service: "Style" },
      { offset: 18, hour: 14, minute: 0, staffId: "7", status: "confirmed", client: "Lola Vega", service: "Color" },
    ]),
  ];
}

function soloEvents(anchor) {
  return [
    {
      id: "today-a1",
      kind: "appointment",
      start: at(anchor, 9, 0),
      end: at(anchor, 10, 0),
      status: "confirmed",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("Maria Santos", "Cut"),
    },
    {
      id: "today-a2",
      kind: "appointment",
      start: at(anchor, 11, 0),
      end: at(anchor, 12, 0),
      status: "pending",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("David Lee", "Beard trim"),
    },
    {
      id: "today-a3",
      kind: "appointment",
      start: at(anchor, 14, 0),
      end: at(anchor, 15, 0),
      status: "completed",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("Sara Cruz", "Color"),
    },
    ...monthScatter(anchor, [
      { offset: 2, hour: 10, minute: 0, staffId: "1", status: "confirmed", client: "Henry Park", service: "Cut" },
      { offset: 5, hour: 14, minute: 0, staffId: "1", status: "pending", client: "Olivia Tan", service: "Style" },
      { offset: 9, hour: 11, minute: 0, staffId: "1", status: "completed", client: "Tom Reyes", service: "Cut" },
      { offset: 14, hour: 15, minute: 0, staffId: "1", status: "confirmed", client: "Pat Morales", service: "Trim" },
    ]),
  ];
}

function busyEvents(anchor) {
  // Lots of overlapping events on today + a heavy scatter day.
  const overlap = (offset, hour, minute, staffId, label) => ({
    offset,
    hour,
    minute,
    staffId,
    status: "confirmed",
    client: label,
    service: "Cut",
  });
  return [
    // Today: overlapping events on each staff
    {
      id: "busy-1",
      kind: "appointment",
      start: at(anchor, 9, 0),
      end: at(anchor, 10, 30),
      status: "confirmed",
      staffId: "1",
      durationMinutes: 90,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("A. First", "Long cut"),
    },
    {
      id: "busy-2",
      kind: "appointment",
      start: at(anchor, 9, 30),
      end: at(anchor, 10, 30),
      status: "pending",
      staffId: "2",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("B. Second", "Color"),
    },
    {
      id: "busy-3",
      kind: "appointment",
      start: at(anchor, 10, 0),
      end: at(anchor, 11, 30),
      status: "confirmed",
      staffId: "3",
      durationMinutes: 90,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("C. Third", "Beard"),
    },
    {
      id: "busy-4",
      kind: "appointment",
      start: at(anchor, 11, 0),
      end: at(anchor, 12, 0),
      status: "confirmed",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("D. Fourth", "Cut"),
    },
    {
      id: "busy-5",
      kind: "appointment",
      start: at(anchor, 13, 0),
      end: at(anchor, 14, 0),
      status: "completed",
      staffId: "2",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("E. Fifth", "Color"),
    },
    {
      id: "busy-block",
      kind: "block",
      start: at(anchor, 12, 0),
      end: at(anchor, 13, 0),
      status: "personal",
      staffId: "3",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: blockHtml("Lunch break"),
    },
    ...monthScatter(anchor, [
      overlap(7, 9, 0, "1", "Heavy 1"),
      overlap(7, 9, 30, "1", "Heavy 2"),
      overlap(7, 10, 0, "2", "Heavy 3"),
      overlap(7, 10, 30, "2", "Heavy 4"),
      overlap(7, 11, 0, "3", "Heavy 5"),
      overlap(7, 11, 30, "3", "Heavy 6"),
      overlap(7, 13, 0, "1", "Heavy 7"),
      overlap(14, 14, 0, "1", "Mid week"),
    ]),
  ];
}

// ---- helpers ----

function todayEvents(anchor) {
  return [
    {
      id: "today-a1",
      kind: "appointment",
      start: at(anchor, 9, 0),
      end: at(anchor, 10, 0),
      status: "confirmed",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("Maria Santos", "Haircut"),
    },
    {
      id: "today-a2",
      kind: "appointment",
      start: at(anchor, 10, 30),
      end: at(anchor, 11, 0),
      status: "pending",
      staffId: "2",
      durationMinutes: 30,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("Sara Cruz", "Color touch-up"),
    },
    {
      id: "today-a3",
      kind: "appointment",
      start: at(anchor, 13, 0),
      end: at(anchor, 14, 0),
      status: "completed",
      staffId: "1",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: bodyHtml("David Lee", "Beard trim"),
    },
    {
      id: "today-b1",
      kind: "block",
      start: at(anchor, 12, 0),
      end: at(anchor, 13, 0),
      status: "personal",
      staffId: "3",
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml: blockHtml("Lunch break"),
    },
  ];
}

// `offsets` is an array of { offset, hour, minute, staffId, status, client, service }.
// `offset` is interpreted as days from the 1st of the anchor's month, so events
// stay rooted in the correct month even when navigating.
function monthScatter(anchor, offsets) {
  const [year, month] = anchor.split("-").map(Number);
  const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
  const day = (offset) => shiftDate(monthStart, offset);
  const anchorDay = parseInt(anchor.split("-")[2], 10);

  return offsets
    .filter(({ offset }) => offset + 1 !== anchorDay) // skip anchor — todayEvents handles it
    .map(({ offset, hour, minute, staffId, status, client, service }, idx) => ({
      id: `month-${idx}`,
      kind: "appointment",
      start: at(day(offset), hour, minute),
      end: at(day(offset), hour + 1, minute),
      status,
      staffId,
      durationMinutes: 60,
      bufferMinutes: 0,
      bodyHtml:
        status === "cancelled"
          ? cancelledBodyHtml(client, service)
          : bodyHtml(client, service),
    }));
}

function todayString() {
  return formatDate(new Date());
}

function formatDate(d) {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

function shiftDate(dateString, days) {
  const [year, month, day] = dateString.split("-").map(Number);
  const d = new Date(year, month - 1, day + days);
  return formatDate(d);
}

function at(dateString, hour, minute = 0) {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return `${dateString}T${hh}:${mm}:00`;
}

function bodyHtml(client, service) {
  return `<p class="text-sm font-bold text-black">${escapeHtml(client)}</p><p class="text-xs text-gray-700">${escapeHtml(service)}</p>`;
}

function cancelledBodyHtml(client, service) {
  return `<p class="text-sm font-bold text-black">${escapeHtml(client)}</p><p class="text-xs text-gray-700">${escapeHtml(service)}</p><p class="text-xs text-gray-600 line-through">cancelled</p>`;
}

function blockHtml(label) {
  return `<p class="text-xs font-medium text-gray-600">${escapeHtml(label)}</p>`;
}

function escapeHtml(value) {
  const div = document.createElement("div");
  div.textContent = String(value);
  return div.innerHTML;
}
